﻿import { Controller, useForm } from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { Box, Button, Icon, Input, Modal, Typography } from 'components/atomic'

import { useAuth } from 'store/auth/hooks'
import { useTournament } from 'store/tournament/hooks'
import { useAppSelector } from 'store/store'

type TeamScoreFormProps = {
  score: number
  opponentScore: number
  screenshots: FileList
}

export const SetScoreModal = () => {
  const { teamScoreModal, closeTeamScoreModal, setTeamScore } = useTournament()
  const { user } = useAuth()
  const [isDisputing, setIsDisputing] = useState(false)

  // Get tournament info to find opponent name and match data
  const tournamentInfo = useAppSelector(
    (state: { tournament: { tournament: any } }) => state.tournament.tournament,
  )

  const {
    isOpen,
    team,
    score,
    matchIndex,
    roundIndex,
    tournamentId,
    pending,
    fullfiled,
    bracketType,
  } = teamScoreModal

  // Find the current match data including any reported scores
  const matchData = useMemo(() => {
    if (!tournamentInfo || matchIndex === undefined || roundIndex === undefined) {
      return null
    }

    let bracketData = tournamentInfo.bracketData || []
    if (bracketType === 'losers' && tournamentInfo.losersBracketData) {
      bracketData = tournamentInfo.losersBracketData
    } else if (bracketType === 'grand-finals' && tournamentInfo.grandFinals) {
      bracketData = [tournamentInfo.grandFinals]
    }

    return bracketData.find(
      (m: any) => m.roundIndex === roundIndex && m.matchIndex === matchIndex
    )
  }, [tournamentInfo, matchIndex, roundIndex, bracketType])

  // Find opponent team name and check if they already submitted
  const { opponentName, opponentSubmission, isTeam1, confirmationStatus } = useMemo(() => {
    if (!matchData || !matchData.participants || !team?.teamId) {
      return { opponentName: 'Opponent', opponentSubmission: null, isTeam1: true, confirmationStatus: 'pending' }
    }

    const teamIndex = matchData.participants.findIndex((p: any) => p.teamId === team.teamId)
    const isTeam1 = teamIndex === 0
    const opponentIndex = isTeam1 ? 1 : 0
    const opponent = matchData.participants[opponentIndex]

    // Check if opponent has submitted scores
    const opponentReport = isTeam1 ? matchData.team2Reported : matchData.team1Reported

    return {
      opponentName: opponent?.name || 'Opponent',
      opponentSubmission: opponentReport || null,
      isTeam1,
      confirmationStatus: matchData.confirmationStatus || 'pending',
    }
  }, [matchData, team?.teamId])

  // Check if opponent has already submitted (we can confirm or dispute)
  const canConfirmOrDispute = opponentSubmission &&
    (confirmationStatus === 'team1_reported' || confirmationStatus === 'team2_reported')

  // Get the scores as opponent reported them (from their perspective)
  // Opponent reports: their score (opponentSubmission.score) and our score (opponentSubmission.opponentScore)
  const opponentReportedTheirScore = opponentSubmission?.score ?? 0
  const opponentReportedOurScore = opponentSubmission?.opponentScore ?? 0

  const { handleSubmit, control, register, reset, setValue } =
    useForm<TeamScoreFormProps>({
      defaultValues: {
        score: score ?? 0,
        opponentScore: 0,
      },
    })

  const close = () => {
    reset()
    setIsDisputing(false)
    closeTeamScoreModal()
  }

  // Handle confirming opponent's submission
  const handleConfirm = () => {
    if (!tournamentId || roundIndex === undefined || matchIndex === undefined || !team) {
      toast.error('Missing required data. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      })
      return
    }

    // When confirming, we submit the same scores the opponent reported
    // From opponent's perspective: they scored opponentReportedTheirScore, we scored opponentReportedOurScore
    // From our perspective: we scored opponentReportedOurScore, they scored opponentReportedTheirScore
    setTeamScore({
      tournamentId,
      matchIndex,
      roundIndex,
      score: opponentReportedOurScore, // Our score (what opponent said we scored)
      opponentScore: opponentReportedTheirScore, // Their score (what opponent said they scored)
      screenshots: undefined,
      teamId: team.teamId,
      bracketType,
    })
  }

  const submitHandler = handleSubmit(
    ({ score, opponentScore, screenshots }: TeamScoreFormProps) => {
      // Validate scores
      if (score === opponentScore) {
        toast.error('Scores cannot be tied', {
          position: 'top-right',
          autoClose: 3000,
        })
        return
      }

      if (score <= 0 && opponentScore <= 0) {
        toast.error('Please enter valid scores', {
          position: 'top-right',
          autoClose: 3000,
        })
        return
      }

      if (
        tournamentId &&
        roundIndex != undefined &&
        roundIndex >= 0 &&
        matchIndex != undefined &&
        matchIndex >= 0 &&
        team
      ) {
        setTeamScore({
          tournamentId,
          matchIndex,
          roundIndex,
          score,
          opponentScore,
          screenshots: screenshots || undefined,
          teamId: team.teamId,
          bracketType,
        })
      } else {
        toast.error('Missing required data. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        })
      }
    },
    (errors) => {
      console.log('Form validation errors:', errors)
    }
  )

  useEffect(() => {
    if (fullfiled === true) {
      toast.success('Score submitted successfully!', {
        position: 'top-right',
        autoClose: 2000,
      })
      reset()
      setIsDisputing(false)
      closeTeamScoreModal()
    } else if (fullfiled === false) {
      toast.error('Failed to submit score. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      })
    }
  }, [fullfiled, reset, closeTeamScoreModal])

  // Reset disputing state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsDisputing(false)
    }
  }, [isOpen])

  return (
    <Modal
      isOpened={isOpen}
      onClose={close}
      title={canConfirmOrDispute && !isDisputing ? 'Confirm Match Result' : (team?.name ?? 'Set Score')}
      withCard={true}
    >
      <Box col>
        {/* Show opponent's submission if they already submitted */}
        {canConfirmOrDispute && !isDisputing ? (
          <div className="flex flex-col gap-4">
            <Typography as="p" className="text-sm text-dark-typo-gray">
              {opponentName} has submitted the match result. Please confirm or dispute.
            </Typography>

            {/* Show the reported scores */}
            <div className="bg-dark-bg-secondary rounded-lg p-4">
              <Typography as="h5" className="mb-3">Reported Result:</Typography>
              <div className="flex justify-between items-center">
                <div className="text-center">
                  <Typography as="p" className="text-sm text-dark-typo-gray">{team?.name}</Typography>
                  <Typography as="h3" className="text-green">{opponentReportedOurScore}</Typography>
                </div>
                <Typography as="h4" className="text-dark-typo-gray">vs</Typography>
                <div className="text-center">
                  <Typography as="p" className="text-sm text-dark-typo-gray">{opponentName}</Typography>
                  <Typography as="h3" className="text-red-400">{opponentReportedTheirScore}</Typography>
                </div>
              </div>
              <div className="mt-2 text-center">
                <Typography as="p" className={`text-sm font-bold ${opponentReportedOurScore > opponentReportedTheirScore ? 'text-green' : 'text-red-400'}`}>
                  {opponentReportedOurScore > opponentReportedTheirScore ? `${team?.name} Wins!` : `${opponentName} Wins!`}
                </Typography>
              </div>
            </div>

            {/* Confirm / Dispute buttons */}
            <div className="flex gap-3">
              <Button
                buttonType="button"
                variant="primary"
                stretch
                loading={pending}
                onClick={handleConfirm}
              >
                Confirm Result
              </Button>
              <Button
                buttonType="button"
                variant="warn"
                type="outline"
                stretch
                onClick={() => setIsDisputing(true)}
              >
                Dispute
              </Button>
            </div>

            <Typography as="p" className="text-xs text-dark-typo-gray">
              If you confirm, the match will be finalized. If you dispute, you can submit your own scores and an admin will review.
            </Typography>
          </div>
        ) : (
          /* Standard score submission form */
          <form onSubmit={submitHandler} className="flex flex-col gap-4">
            {isDisputing && (
              <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-3 mb-2">
                <Typography as="p" className="text-sm text-yellow-400">
                  You are disputing {opponentName}'s submission. Enter what you believe the correct scores are.
                </Typography>
              </div>
            )}

            {/* Screenshot upload */}
            <Controller
              name="screenshots"
              control={control}
              render={({ field }) => (
                <span className="flex flex-col gap-2">
                  <Typography as="h6">
                    Screenshots (optional){' '}
                    {field.value?.length ? `(${field.value?.length})` : ''}
                  </Typography>
                  <label htmlFor="screenshot-input" className="z-10 w-fit">
                    <Box
                      className="w-32 h-32 rounded-lg bg-gradient-primary-dark cursor-pointer hover:opacity-80"
                      justifyCenter
                      alignCenter
                      style={
                        field.value?.length
                          ? {
                              backgroundImage: `url(${URL.createObjectURL(
                                field.value[field.value.length - 1],
                              )})`,
                              backgroundSize: 'cover',
                            }
                          : {}
                      }
                    >
                      <Icon name="camera" />
                    </Box>
                    <input
                      {...register('screenshots')}
                      id="screenshot-input"
                      accept="image/*"
                      className="hidden"
                      type="file"
                      multiple
                      onChange={(e) => {
                        const fileList = e.target.files
                        if (!fileList) return
                        field.onChange(fileList)
                      }}
                    />
                  </label>
                </span>
              )}
            ></Controller>

            {/* Your team's score */}
            <Controller
              name="score"
              control={control}
              rules={{
                required: 'Your score is required',
                validate: (value: number) => {
                  if (value < 0) return 'Score must be 0 or greater'
                  return true
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <span className="flex flex-col gap-2">
                  <label htmlFor="score-input">
                    <Typography as="h6" className="text-green">
                      Your Score ({team?.name})
                    </Typography>
                  </label>
                  <Input
                    id="score-input"
                    {...field}
                    placeholder="Your score"
                    border="full"
                    stretch
                    type="number"
                    inputMode="numeric"
                    onChange={(e) => {
                      const value = e.target.value
                      if (/^[0-9]*$/.test(value)) {
                        field.onChange(parseInt(value) || 0)
                      }
                    }}
                  />
                  {error && <Typography as="p" className="text-red-500">{error.message}</Typography>}
                </span>
              )}
            ></Controller>

            {/* Opponent's score */}
            <Controller
              name="opponentScore"
              control={control}
              rules={{
                required: 'Opponent score is required',
                validate: (value: number) => {
                  if (value < 0) return 'Score must be 0 or greater'
                  return true
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <span className="flex flex-col gap-2">
                  <label htmlFor="opponent-score-input">
                    <Typography as="h6" className="text-red-400">
                      Opponent Score ({opponentName})
                    </Typography>
                  </label>
                  <Input
                    id="opponent-score-input"
                    {...field}
                    placeholder="Opponent score"
                    border="full"
                    stretch
                    type="number"
                    inputMode="numeric"
                    onChange={(e) => {
                      const value = e.target.value
                      if (/^[0-9]*$/.test(value)) {
                        field.onChange(parseInt(value) || 0)
                      }
                    }}
                  />
                  {error && <Typography as="p" className="text-red-500">{error.message}</Typography>}
                </span>
              )}
            ></Controller>

            {/* Info text */}
            <Typography as="p" className="text-xs text-dark-typo-gray">
              {isDisputing
                ? 'Submit your version of the scores. An admin will review both submissions and determine the result.'
                : 'Your score will be submitted for confirmation. The opponent can confirm or dispute. An admin can also finalize the result.'}
            </Typography>

            <div className="flex gap-3">
              {isDisputing && (
                <Button
                  buttonType="button"
                  type="outline"
                  stretch
                  onClick={() => setIsDisputing(false)}
                >
                  Cancel
                </Button>
              )}
              <Button buttonType="submit" loading={pending} stretch>
                {isDisputing ? 'Submit Dispute' : 'Submit Score'}
              </Button>
            </div>
          </form>
        )}
      </Box>
    </Modal>
  )
}
